// Convenção: cada lista de chaves vive em um objeto factory para evitar typos
// e centralizar a invalidação.

export const queryKeys = {
  companies: {
    all: ['companies'] as const,
    list: () => [...queryKeys.companies.all, 'list'] as const,
  },
  agents: {
    all: ['agents'] as const,
    list: (companyId: string) =>
      [...queryKeys.agents.all, 'list', companyId] as const,
    detail: (agentId: string) =>
      [...queryKeys.agents.all, 'detail', agentId] as const,
    executionConfig: (agentId: string) =>
      [...queryKeys.agents.all, 'executionConfig', agentId] as const,
    executionModes: () =>
      [...queryKeys.agents.all, 'executionModes'] as const,
    adapterConfig: (agentId: string) =>
      [...queryKeys.agents.all, 'adapterConfig', agentId] as const,
    sharedConfig: (agentId: string) =>
      [...queryKeys.agents.all, 'sharedConfig', agentId] as const,
    inbox: (agentId: string) =>
      [...queryKeys.agents.all, 'inbox', agentId] as const,
  },
  athenaRecommendations: {
    all: ['athenaRecommendations'] as const,
    /**
     * `filters` é incluído na chave para que filtros diferentes virem caches
     * separados. TanStack faz deep-equal — passar `{}` quando não há filtros
     * mantém a chave estável entre re-renders.
     */
    list: (companyId: string, filters: Record<string, unknown> = {}) =>
      [
        ...queryKeys.athenaRecommendations.all,
        'list',
        companyId,
        filters,
      ] as const,
    detail: (recommendationId: string) =>
      [
        ...queryKeys.athenaRecommendations.all,
        'detail',
        recommendationId,
      ] as const,
  },
  adapters: {
    all: ['adapters'] as const,
    runtimeProfiles: () =>
      [...queryKeys.adapters.all, 'runtimeProfiles'] as const,
    list: (companyId: string) =>
      [...queryKeys.adapters.all, 'list', companyId] as const,
    fields: (adapterId: string, scope?: string) =>
      [...queryKeys.adapters.all, 'fields', adapterId, scope ?? 'all'] as const,
    // W5 — valores per-company de um adapter (company_adapter_values).
    // PRIMARY pra credenciais compartilhadas; agent override fica em
    // agent_adapter_configs (queryKeys.agents.adapterConfig).
    companyValues: (companyId: string, adapterId: string) =>
      [...queryKeys.adapters.all, 'companyValues', companyId, adapterId] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    list: (companyId: string) =>
      [...queryKeys.tasks.all, 'list', companyId] as const,
  },
  goals: {
    all: ['goals'] as const,
    list: (companyId: string) =>
      [...queryKeys.goals.all, 'list', companyId] as const,
  },
  heartbeats: {
    all: ['heartbeats'] as const,
    list: (companyId: string) =>
      [...queryKeys.heartbeats.all, 'list', companyId] as const,
  },
  approvals: {
    all: ['approvals'] as const,
    list: (companyId: string) =>
      [...queryKeys.approvals.all, 'list', companyId] as const,
  },
  auditLog: {
    all: ['auditLog'] as const,
    list: (companyId: string) =>
      [...queryKeys.auditLog.all, 'list', companyId] as const,
  },
  workflowSteps: {
    all: ['workflowSteps'] as const,
    list: (companyId: string) =>
      [...queryKeys.workflowSteps.all, 'list', companyId] as const,
  },
  workflowTriggerTypes: {
    all: ['workflowTriggerTypes'] as const,
    list: () => [...queryKeys.workflowTriggerTypes.all, 'list'] as const,
  },
  workflowLogicPatterns: {
    all: ['workflowLogicPatterns'] as const,
    list: () => [...queryKeys.workflowLogicPatterns.all, 'list'] as const,
  },
  workflowDefinitions: {
    all: ['workflowDefinitions'] as const,
    list: (companyId: string) =>
      [...queryKeys.workflowDefinitions.all, 'list', companyId] as const,
    detail: (companyId: string, slug: string) =>
      [
        ...queryKeys.workflowDefinitions.all,
        'detail',
        companyId,
        slug,
      ] as const,
  },
  llmModels: {
    all: ['llmModels'] as const,
  },
  agentSpecialties: {
    all: ['agentSpecialties'] as const,
  },
  agentSpecialtyConfigs: {
    all: ['agentSpecialtyConfigs'] as const,
    forAgent: (agentId: string) =>
      [...queryKeys.agentSpecialtyConfigs.all, 'forAgent', agentId] as const,
  },
  routines: {
    all: ['routines'] as const,
    list: (companyId: string) =>
      [...queryKeys.routines.all, 'list', companyId] as const,
    detail: (routineId: string) =>
      [...queryKeys.routines.all, 'detail', routineId] as const,
    byAgent: (agentId: string) =>
      [...queryKeys.routines.all, 'byAgent', agentId] as const,
  },
  runs: {
    all: ['runs'] as const,
    list: (companyId: string, agentId?: string) =>
      agentId
        ? (['runs', 'list', companyId, agentId] as const)
        : (['runs', 'list', companyId] as const),
    detail: (runId: string) => ['runs', 'detail', runId] as const,
  },
  projects: {
    all: ['projects'] as const,
    list: (companyId: string) =>
      [...queryKeys.projects.all, 'list', companyId] as const,
    detail: (projectId: string) =>
      [...queryKeys.projects.all, 'detail', projectId] as const,
  },
  transcripts: {
    all: ['transcripts'] as const,
    detail: (runId: string) =>
      [...queryKeys.transcripts.all, 'detail', runId] as const,
  },
  companySecrets: {
    all: ['companySecrets'] as const,
    list: (companyId: string) =>
      [...queryKeys.companySecrets.all, 'list', companyId] as const,
  },
  plugins: {
    all: ['plugins'] as const,
    marketplace: () => [...queryKeys.plugins.all, 'marketplace'] as const,
    manifests: () => [...queryKeys.plugins.all, 'manifests'] as const,
    manifest: (pluginId: string) =>
      [...queryKeys.plugins.all, 'manifest', pluginId] as const,
    installed: (companyId: string) =>
      [...queryKeys.plugins.all, 'installed', companyId] as const,
  },
  sipocSectors: {
    all: ['sipocSectors'] as const,
    list: (companyId: string) =>
      [...queryKeys.sipocSectors.all, 'list', companyId] as const,
  },
  sipocPositions: {
    all: ['sipocPositions'] as const,
    list: (companyId: string) =>
      [...queryKeys.sipocPositions.all, 'list', companyId] as const,
  },
  sipocComponents: {
    all: ['sipocComponents'] as const,
    list: (processId: string) =>
      [...queryKeys.sipocComponents.all, 'list', processId] as const,
  },
  sipocProcess: {
    all: ['sipocProcess'] as const,
    detail: (processId: string) =>
      [...queryKeys.sipocProcess.all, 'detail', processId] as const,
  },
  sipocAnalytics: {
    all: ['sipocAnalytics'] as const,
  },
  sipocTemplates: {
    all: ['sipocTemplates'] as const,
  },
  rag: {
    all: ['rag'] as const,
    list: (companyId: string) =>
      [...queryKeys.rag.all, 'list', companyId] as const,
    detail: (documentId: string) =>
      [...queryKeys.rag.all, 'detail', documentId] as const,
  },
  prospects: {
    all: ['prospects'] as const,
    list: (companyId: string) =>
      [...queryKeys.prospects.all, 'list', companyId] as const,
    detail: (prospectId: string) =>
      [...queryKeys.prospects.all, 'detail', prospectId] as const,
  },
  researchTemplates: {
    all: ['researchTemplates'] as const,
    list: (companyId: string) =>
      [...queryKeys.researchTemplates.all, 'list', companyId] as const,
  },
  cnpjLookup: {
    all: ['cnpjLookup'] as const,
    detail: (cnpj: string) =>
      [...queryKeys.cnpjLookup.all, 'detail', cnpj] as const,
  },
  appUsers: {
    all: ['appUsers'] as const,
    list: (companyId: string) =>
      [...queryKeys.appUsers.all, 'list', companyId] as const,
  },
  // W3 PRD Fundação Orchestration — connector_sessions (WhatsApp Meta + futuros canais)
  connectorSessions: {
    all: ['connectorSessions'] as const,
    list: (companyId: string, filters: Record<string, unknown> = {}) =>
      [...queryKeys.connectorSessions.all, 'list', companyId, filters] as const,
    detail: (sessionId: string) =>
      [...queryKeys.connectorSessions.all, 'detail', sessionId] as const,
  },
  connectorChannels: {
    all: ['connectorChannels'] as const,
    list: () => [...queryKeys.connectorChannels.all, 'list'] as const,
  },
  // W3.1 — health do webhook Meta WhatsApp Cloud API (oficial). Usado em AdminConnectors.
  metaWhatsappWebhook: {
    all: ['metaWhatsappWebhook'] as const,
    health: () => [...queryKeys.metaWhatsappWebhook.all, 'health'] as const,
  },
  // W11 — catálogo de templates Meta WABA (sync + listagem em AdminConnectors).
  whatsappTemplates: {
    all: ['whatsappTemplates'] as const,
    list: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.whatsappTemplates.all, 'list', filters] as const,
  },
  mcp: {
    all: ['mcp'] as const,
    servers: () => [...queryKeys.mcp.all, 'servers'] as const,
    server: (serverId: string) => [...queryKeys.mcp.all, 'server', serverId] as const,
    agentBindings: (agentId: string) =>
      [...queryKeys.mcp.all, 'agentBindings', agentId] as const,
    // N12 — credenciais MCP no nível company (company_mcp_values). PRIMARY;
    // o binding do agente só referencia o server, herdando estas creds.
    companyValues: (companyId: string) =>
      [...queryKeys.mcp.all, 'companyValues', companyId] as const,
  },
  bpmnDiagrams: {
    all: ['bpmnDiagrams'] as const,
    list: (companyId: string, filters: Record<string, unknown> = {}) =>
      [...queryKeys.bpmnDiagrams.all, 'list', companyId, filters] as const,
    detail: (diagramId: string) =>
      [...queryKeys.bpmnDiagrams.all, 'detail', diagramId] as const,
    versions: (diagramId: string) =>
      [...queryKeys.bpmnDiagrams.all, 'versions', diagramId] as const,
  },
  agentDomains: {
    all: ['agentDomains'] as const,
    list: () => [...queryKeys.agentDomains.all, 'list'] as const,
  },
  intelligence: {
    all: ['intelligence'] as const,
    dashboard: (weeks: number) =>
      [...queryKeys.intelligence.all, 'dashboard', weeks] as const,
  },
} as const
